(() => {
  'use strict';

  const contextElement = document.getElementById('hdt-analytics-context');
  if (!contextElement) return;

  let context;
  try {
    context = JSON.parse(contextElement.textContent);
  } catch (error) {
    console.warn('[HDT data layer] Invalid analytics context.', error);
    return;
  }

  window.dataLayer = window.dataLayer || [];

  const analyticsStorageKey = 'hdt:analytics:customer-id';
  const attributionStorageKey = 'hdt:analytics:item-attribution';
  const emittedEvents = new Map();
  const pendingCartAdditions = new Map();
  const observedCards = new WeakSet();
  const impressedCards = new WeakSet();
  const variantObservedSwatches = new WeakSet();
  let listContextCache = new WeakMap();
  let tabObserver;
  let cartMutationObserver;

  const toNumber = (value) => {
    const number = Number(value);
    return Number.isFinite(number) ? number : 0;
  };

  const minorToMajor = (value) => Number((toNumber(value) / 100).toFixed(2));
  const cleanString = (value) => (value == null ? '' : String(value).trim());
  const rawItems = (payload) => {
    if (!payload) return [];
    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload.items)) return payload.items;
    return [payload];
  };

  const compactObject = (object) =>
    Object.fromEntries(
      Object.entries(object).filter(([, value]) => {
        if (value === undefined || value === null || value === '') return false;
        if (Array.isArray(value) && value.length === 0) return false;
        return true;
      })
    );

  const eventId = (eventName) => {
    const id = window.crypto?.randomUUID?.() || `${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
    return `${eventName}_${id}`;
  };

  const shouldEmit = (key, windowMs = 1000) => {
    if (!key) return true;
    const now = Date.now();
    const previous = emittedEvents.get(key);
    emittedEvents.set(key, now);
    if (emittedEvents.size > 100) {
      emittedEvents.forEach((time, entryKey) => {
        if (now - time > 60000) emittedEvents.delete(entryKey);
      });
    }
    return !previous || now - previous > windowMs;
  };

  const safeStorage = {
    get(storage, key) {
      try {
        return storage.getItem(key);
      } catch (_) {
        return null;
      }
    },
    set(storage, key, value) {
      try {
        storage.setItem(key, value);
      } catch (_) {
        // Analytics storage must never interfere with the storefront.
      }
    },
    remove(storage, key) {
      try {
        storage.removeItem(key);
      } catch (_) {
        // Analytics storage must never interfere with the storefront.
      }
    },
  };

  const posthogCapture = (name, properties) => {
    if (window.posthog?.capture) window.posthog.capture(name, properties);
  };

  const initializeIdentity = () => {
    const customerId = context.customerId ? String(context.customerId) : null;
    window.dataLayer.push({
      user_id: customerId,
      customer_status: customerId ? 'logged_in' : 'guest',
    });

    if (!window.posthog) return;
    const previousCustomerId = safeStorage.get(window.localStorage, analyticsStorageKey);

    if (customerId) {
      if (previousCustomerId && previousCustomerId !== customerId && window.posthog.reset) {
        window.posthog.reset();
      }
      window.posthog.identify?.(customerId);
      safeStorage.set(window.localStorage, analyticsStorageKey, customerId);
    } else if (previousCustomerId) {
      window.posthog.reset?.();
      safeStorage.remove(window.localStorage, analyticsStorageKey);
    }
  };

  const normalizeDiscountCodes = (item) => {
    const codes =
      item.discount_codes ||
      item.discounts ||
      item.line_level_discount_allocations ||
      item.cart_level_discount_applications ||
      [];
    return rawItems(codes)
      .map((discount) => cleanString(discount?.title || discount?.code || discount))
      .filter(Boolean);
  };

  const normalizeItem = (item, overrides = {}) => {
    if (!item) return null;

    const variantId = overrides.variantId || item.variant_id || item.variantId || item.id;
    const productId = item.product_id || item.productId;
    const lineQuantity = Math.max(1, toNumber(item.quantity) || 1);
    const quantity = Math.max(1, toNumber(overrides.quantity ?? lineQuantity) || 1);
    const allAllocationTotal = rawItems(item.discount_allocations).reduce(
      (total, allocation) => total + toNumber(allocation?.amount),
      0
    );
    const lineAllocationTotal = rawItems(item.line_level_discount_allocations).reduce(
      (total, allocation) => total + toNumber(allocation?.amount),
      0
    );
    const cartAllocationPerUnit =
      Math.max(0, allAllocationTotal - lineAllocationTotal) / lineQuantity;
    const baseRawPrice = item.final_price ?? item.discounted_price ?? item.price;
    const rawPrice =
      baseRawPrice === undefined || baseRawPrice === null || baseRawPrice === ''
        ? baseRawPrice
        : Math.max(0, toNumber(baseRawPrice) - cartAllocationPerUnit);
    const rawOriginalPrice = item.original_price ?? item.compare_at_price ?? rawPrice;
    const discountCodes = normalizeDiscountCodes(item);
    const normalized = {
      item_id: cleanString(variantId || productId || item.handle),
      item_name: cleanString(item.product_title || item.product?.title || item.title),
      affiliation: cleanString(context.storeName),
      coupon: discountCodes.join(', '),
      quantity,
      item_brand: cleanString(item.vendor || item.product?.vendor),
      item_category: cleanString(item.product_type || item.product?.type),
      item_category2: cleanString(item.collection_title),
      item_variant: cleanString(item.variant_title || item.variant?.title),
      item_group_id: productId ? String(productId) : '',
      item_list_id: cleanString(overrides.itemListId || item.item_list_id),
      item_list_name: cleanString(overrides.itemListName || item.item_list_name),
      index:
        overrides.index !== undefined
          ? toNumber(overrides.index)
          : item.index !== undefined
            ? toNumber(item.index)
            : undefined,
      id: cleanString(variantId || productId || item.handle),
      google_business_vertical: 'retail',
      item_sku: cleanString(item.sku),
      item_url: cleanString(item.url),
    };

    if (rawPrice !== undefined && rawPrice !== null && rawPrice !== '') {
      normalized.price = minorToMajor(rawPrice);
    }

    const originalPrice = minorToMajor(rawOriginalPrice);
    const currentPrice = minorToMajor(rawPrice);
    const allocatedDiscount =
      minorToMajor(Math.max(toNumber(item.total_discount), allAllocationTotal)) / lineQuantity;
    const discount = Math.max(0, originalPrice - currentPrice, allocatedDiscount);
    if (discount > 0) normalized.discount = Number(discount.toFixed(2));

    const compact = compactObject(normalized);
    return compact.item_id || compact.item_name ? compact : null;
  };

  const ecommerceValue = (items) =>
    Number(
      items
        .reduce((total, item) => total + toNumber(item.price) * Math.max(1, toNumber(item.quantity)), 0)
        .toFixed(2)
    );

  const pushEcommerceEvent = (name, items, options = {}) => {
    const normalizedItems = rawItems(items).filter(Boolean);
    if (!normalizedItems.length || !shouldEmit(options.dedupeKey, options.dedupeWindow)) return false;

    const ecommerce = compactObject({
      currency: context.currency,
      items: normalizedItems,
      ...options.ecommerce,
    });
    if (
      normalizedItems.some((item) => typeof item.price === 'number') &&
      typeof ecommerce.value !== 'number'
    ) {
      ecommerce.value = ecommerceValue(normalizedItems);
    }

    const id = eventId(name);
    window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push({
      event: name,
      event_id: id,
      event_source: 'kalles_theme',
      ...options.event,
      ecommerce,
    });
    posthogCapture(name, {
      event_id: id,
      event_source: 'kalles_theme',
      currency: ecommerce.currency,
      value: ecommerce.value,
      items: ecommerce.items,
      ...options.posthog,
    });
    return true;
  };

  const pushSearchEvent = (term, resultCount) => {
    const originalTerm = cleanString(term);
    if (!originalTerm) return;
    const key = `search:${originalTerm}:${resultCount ?? ''}`;
    if (!shouldEmit(key, 2000)) return;

    const id = eventId('search');
    const payload = compactObject({
      event: 'search',
      event_id: id,
      event_source: 'shopify_search_results',
      search_term: 'site_search',
      search_query_length: originalTerm.length,
      result_count: resultCount,
    });
    window.dataLayer.push(payload);
    posthogCapture('search', payload);
  };

  const readAttributions = () => {
    try {
      return JSON.parse(safeStorage.get(window.sessionStorage, attributionStorageKey) || '{}');
    } catch (_) {
      return {};
    }
  };

  const rememberAttributions = (items) => {
    const attributableItems = rawItems(items).filter(
      (item) => item?.item_id && item.item_list_id
    );
    if (!attributableItems.length) return;
    const attributions = readAttributions();
    attributableItems.forEach((item) => {
      attributions[item.item_id] = {
        item_list_id: item.item_list_id,
        item_list_name: item.item_list_name,
        index: item.index,
        saved_at: Date.now(),
      };
    });
    const trimmed = Object.fromEntries(
      Object.entries(attributions)
        .sort(([, left], [, right]) => right.saved_at - left.saved_at)
        .slice(0, 30)
    );
    safeStorage.set(window.sessionStorage, attributionStorageKey, JSON.stringify(trimmed));
  };
  const rememberAttribution = (item) => rememberAttributions([item]);

  const applyRememberedAttribution = (item, attributions = readAttributions()) => {
    const attribution = item?.item_id ? attributions[item.item_id] : null;
    return attribution ? compactObject({ ...item, ...attribution, saved_at: undefined }) : item;
  };

  let cartState = context.cart || { items: [], item_count: 0, total_price: 0 };
  let cartStateRevision = 0;
  let cartSyncPromise = null;
  let cartSyncRequested = 0;
  let cartSyncProcessed = 0;

  const stableProperties = (properties) => {
    if (!properties || typeof properties !== 'object') return '';
    return Object.keys(properties)
      .sort()
      .map((key) => `${key}:${cleanString(properties[key])}`)
      .join('|');
  };

  const itemFingerprint = (item) =>
    [
      item.variant_id || item.id || item.product_id,
      item.selling_plan_id || item.selling_plan_allocation?.selling_plan?.id || '',
      stableProperties(item.properties),
    ].join('::');

  const itemQuantities = (cart) => {
    const quantities = new Map();
    rawItems(cart?.items).forEach((item) => {
      const key = itemFingerprint(item);
      const existing = quantities.get(key);
      if (existing) {
        existing.quantity += toNumber(item.quantity);
      } else {
        quantities.set(key, { item, quantity: toNumber(item.quantity) });
      }
    });
    return quantities;
  };

  const addProductContext = (item, previousItem = null) => {
    const productId = cleanString(item?.product_id || item?.productId);
    const pageProduct =
      context.product && cleanString(context.product.product_id) === productId
        ? context.product
        : null;
    const card = Array.from(document.querySelectorAll('hdt-card-product')).find(
      (candidate) => cleanString(candidate.dataset.analyticsProductId) === productId
    );

    return {
      ...item,
      product_type:
        cleanString(item?.product_type) ||
        cleanString(previousItem?.product_type) ||
        cleanString(pageProduct?.product_type) ||
        cleanString(card?.dataset.analyticsCategory),
      collection_title:
        cleanString(item?.collection_title) ||
        cleanString(previousItem?.collection_title) ||
        cleanString(pageProduct?.collection_title) ||
        cleanString(card?.dataset.analyticsCategory2),
    };
  };

  const enrichCartState = (nextCart, previousCart = cartState) => {
    const previousItems = new Map(
      rawItems(previousCart?.items).map((item) => [itemFingerprint(item), item])
    );
    return {
      ...nextCart,
      items: rawItems(nextCart?.items).map((item) =>
        addProductContext(item, previousItems.get(itemFingerprint(item)))
      ),
    };
  };

  const trackCartDiff = (previousCart, nextCart) => {
    const previous = itemQuantities(previousCart);
    const next = itemQuantities(nextCart);
    const keys = new Set([...previous.keys(), ...next.keys()]);
    const additions = [];
    const removals = [];
    const rememberedAttributions = readAttributions();

    keys.forEach((key) => {
      const before = previous.get(key);
      const after = next.get(key);
      let difference = (after?.quantity || 0) - (before?.quantity || 0);
      if (!difference) return;

      if (difference > 0) {
        const pending = pendingCartAdditions.get(key);
        const pendingQuantity = pending?.expiresAt > Date.now() ? pending.quantity : 0;
        const consumedQuantity = Math.min(difference, pendingQuantity);
        difference -= consumedQuantity;
        if (pendingQuantity > consumedQuantity) {
          pendingCartAdditions.set(key, {
            quantity: pendingQuantity - consumedQuantity,
            expiresAt: pending.expiresAt,
          });
        } else {
          pendingCartAdditions.delete(key);
        }
        if (!difference) return;
      }

      const sourceItem = difference > 0 ? after?.item : before?.item;
      const normalized = applyRememberedAttribution(
        normalizeItem(sourceItem, { quantity: Math.abs(difference) }),
        rememberedAttributions
      );
      if (!normalized) return;
      (difference > 0 ? additions : removals).push(normalized);
    });
    pendingCartAdditions.forEach((pending, key) => {
      if (pending.expiresAt <= Date.now()) pendingCartAdditions.delete(key);
    });

    if (additions.length) {
      pushEcommerceEvent('add_to_cart', additions);
    }
    if (removals.length) {
      pushEcommerceEvent('remove_from_cart', removals);
    }
  };

  const acceptCartState = (nextCart) => {
    if (!nextCart?.items || typeof nextCart.item_count !== 'number') return;
    const enrichedCart = enrichCartState(nextCart);
    trackCartDiff(cartState, enrichedCart);
    cartState = enrichedCart;
    cartStateRevision += 1;
    context.currency = enrichedCart.currency || context.currency;
  };

  const trackProductFormAddition = (detail) => {
    const response = detail.cartData;
    if (detail.source !== 'product-form-component') return;
    if (response?.items && typeof response.item_count === 'number') return;

    const responseItems = rawItems(response).filter(
      (item) => item && (item.variant_id || item.id || item.product_id)
    );
    if (!responseItems.length) return;
    const rememberedAttributions = readAttributions();

    const additions = responseItems
      .map((item) => {
        const quantity =
          responseItems.length === 1
            ? Math.max(1, toNumber(detail.data?.itemCount || item.quantity))
            : Math.max(1, toNumber(item.quantity));
        const key = itemFingerprint(item);
        const pending = pendingCartAdditions.get(key);
        pendingCartAdditions.set(key, {
          quantity: (pending?.quantity || 0) + quantity,
          expiresAt: Date.now() + 15000,
        });
        return applyRememberedAttribution(
          normalizeItem(addProductContext(item), { quantity }),
          rememberedAttributions
        );
      })
      .filter(Boolean);

    if (additions.length) pushEcommerceEvent('add_to_cart', additions);
  };

  const fetchWithTimeout = async (url, options, timeout = 4000) => {
    const controller = new AbortController();
    const timer = window.setTimeout(() => controller.abort(), timeout);
    try {
      return await fetch(url, { ...options, signal: controller.signal });
    } finally {
      window.clearTimeout(timer);
    }
  };

  const fetchCartState = async (rootUrl) => {
    try {
      const sectionResponse = await fetchWithTimeout(
        `${rootUrl}?section_id=hdt-analytics-cart-context`,
        {
          credentials: 'same-origin',
          headers: { Accept: 'text/html' },
        }
      );
      if (sectionResponse.ok) {
        const documentFragment = new DOMParser().parseFromString(
          await sectionResponse.text(),
          'text/html'
        );
        const cartContext = documentFragment.querySelector('[data-hdt-cart-context]')?.textContent;
        if (cartContext) return JSON.parse(cartContext);
      }
    } catch (_) {
      // Fall through to the official Ajax cart endpoint.
    }

    try {
      const ajaxResponse = await fetchWithTimeout(`${rootUrl}cart.js`, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      });
      return ajaxResponse.ok ? ajaxResponse.json() : null;
    } catch (_) {
      return null;
    }
  };

  const reconcileCart = () => {
    cartSyncRequested += 1;
    if (cartSyncPromise) return cartSyncPromise;

    cartSyncPromise = Promise.resolve()
      .then(async () => {
        while (cartSyncProcessed < cartSyncRequested) {
          const requestNumber = cartSyncRequested;
          const revisionBeforeFetch = cartStateRevision;
          const rootUrl = `${context.rootUrl || '/'}`.replace(/\/?$/, '/');
          const responseCart = await fetchCartState(rootUrl);
          if (responseCart) {
            if (revisionBeforeFetch === cartStateRevision) {
              acceptCartState(responseCart);
            } else {
              cartSyncRequested += 1;
            }
          }
          cartSyncProcessed = requestNumber;
        }
      })
      .catch(() => {
        // Analytics must never interfere with the storefront cart.
      })
      .finally(() => {
        cartSyncPromise = null;
      });
    return cartSyncPromise;
  };

  const cardRawItem = (card) => {
    const productLink = card.querySelector(
      '.hdt-card-product__title[data-pr-url], .hdt-card-product__title a, a[data-pr-url]'
    );
    const titleElement =
      card.querySelector('.hdt-card-product__title') || productLink || card.querySelector('[data-product-title]');
    const wishlist = card.querySelector('hdt-wishlist[data-id]');
    const variantInput = card.querySelector('input[name="id"]');
    const productUrl =
      card.dataset.analyticsUrl || productLink?.href || productLink?.getAttribute('data-pr-url') || '';
    const handle = productUrl ? productUrl.split('/products/')[1]?.split(/[?#]/)[0] : '';
    const productId = card.dataset.analyticsProductId || wishlist?.dataset.id || handle;
    const variantId = card.dataset.analyticsVariantId || variantInput?.value;
    if (!productId && !variantId) return null;

    return {
      product_id: productId,
      variant_id: variantId,
      product_title: card.dataset.analyticsTitle || titleElement?.textContent?.trim(),
      variant_title: card.dataset.analyticsVariantTitle,
      vendor: card.dataset.analyticsVendor,
      product_type: card.dataset.analyticsCategory,
      collection_title: card.dataset.analyticsCategory2,
      sku: card.dataset.analyticsSku,
      price: card.dataset.analyticsPrice,
      original_price: card.dataset.analyticsOriginalPrice,
      quantity: 1,
      url: productUrl,
    };
  };

  const listContextForCard = (card) => {
    const section = card.closest('[id^="shopify-section-"]') || card.closest('section') || card.parentElement;
    const subgroup = card.closest('[tab-item], [role="tabpanel"], [data-tab-item], [data-tabs-content]');
    const listRoot = subgroup || section;
    const cached = listContextCache.get(listRoot);
    if (cached) {
      return {
        itemListId: cached.itemListId,
        itemListName: cached.itemListName,
        index: cached.indexes.get(card) ?? 0,
      };
    }

    const sectionId =
      section?.id?.replace(/^shopify-section-/, '') || context.collection?.handle || context.pageType;
    const subgroupId =
      subgroup?.id || subgroup?.dataset?.tabItem || subgroup?.dataset?.tabsContent;
    const labelledBy = subgroup?.getAttribute('aria-labelledby');
    let subgroupLabel = labelledBy ? document.getElementById(labelledBy)?.textContent?.trim() : '';
    if (!subgroupLabel && subgroup?.hasAttribute('tab-item')) {
      const tabItems = Array.from(subgroup.parentElement?.querySelectorAll(':scope > [tab-item]') || []);
      const tabIndex = tabItems.indexOf(subgroup);
      const tabButtons = section?.querySelectorAll('hdt-tab button') || [];
      subgroupLabel = tabIndex >= 0 ? tabButtons[tabIndex]?.textContent?.trim() : '';
    }
    const heading = Array.from(
      section?.querySelectorAll('h1, h2, h3, h4, [data-heading]') || []
    ).find((element) => !element.closest('hdt-card-product'))?.textContent?.trim();
    const candidates = Array.from(listRoot?.querySelectorAll('hdt-card-product') || []).filter(
      (candidate) => candidate.offsetParent !== null
    );
    const metadata = {
      itemListId: cleanString(subgroupId ? `${sectionId}:${subgroupId}` : sectionId),
      itemListName: cleanString(
        subgroupLabel || heading || context.collection?.title || context.pageTitle || context.pageType
      ),
      indexes: new Map(candidates.map((candidate, index) => [candidate, index])),
    };
    listContextCache.set(listRoot, metadata);
    return {
      itemListId: metadata.itemListId,
      itemListName: metadata.itemListName,
      index: metadata.indexes.get(card) ?? 0,
    };
  };

  const productCardItem = (card) => {
    if (!card) return null;
    const list = listContextForCard(card);
    return normalizeItem(cardRawItem(card), list);
  };

  const productCards = (scope = document) => {
    const cards = Array.from(scope.querySelectorAll?.('hdt-card-product') || []);
    if (scope instanceof Element && scope.matches('hdt-card-product')) cards.unshift(scope);
    return cards;
  };

  const variantUrl = (url, variantId) => {
    if (!url || !variantId) return cleanString(url);
    try {
      const parsedUrl = new URL(url, location.origin);
      parsedUrl.searchParams.set('variant', variantId);
      return parsedUrl.origin === location.origin
        ? `${parsedUrl.pathname}${parsedUrl.search}${parsedUrl.hash}`
        : parsedUrl.href;
    } catch (_) {
      return cleanString(url);
    }
  };

  const updateCardVariantContext = (card, variant) => {
    if (!card || !variant) return;
    card.dataset.analyticsVariantId = variant.id || card.dataset.analyticsVariantId;
    card.dataset.analyticsVariantTitle = variant.title || card.dataset.analyticsVariantTitle;
    card.dataset.analyticsSku = variant.sku || card.dataset.analyticsSku;
    card.dataset.analyticsPrice = variant.price ?? card.dataset.analyticsPrice;
    card.dataset.analyticsOriginalPrice =
      variant.compare_at_price || variant.price || card.dataset.analyticsOriginalPrice;

    if (!variant.id) return;
    const productLinks = card.querySelectorAll(
      '.hdt-card-product__title[data-pr-url], a[data-pr-url], a[href*="/products/"]'
    );
    productLinks.forEach((link) => {
      const selectedUrl = variantUrl(link.getAttribute('href'), variant.id);
      if (selectedUrl) link.setAttribute('href', selectedUrl);
    });
    card.dataset.analyticsUrl = variantUrl(card.dataset.analyticsUrl, variant.id);
  };

  const observeCardVariantChanges = (card) => {
    card.querySelectorAll('hdt-swatch-card').forEach((swatch) => {
      if (variantObservedSwatches.has(swatch)) return;
      variantObservedSwatches.add(swatch);
      swatch.addEventListener('variant:updated', (event) => {
        updateCardVariantContext(card, event.detail?.variant);
        window.requestAnimationFrame(() => observeCardVariantChanges(card));
      });
    });
  };

  const trackCardImpressions = (cards) => {
    const groups = new Map();
    const attributions = [];
    cards.forEach((card) => {
      if (impressedCards.has(card) || card.offsetParent === null || card.closest('[hidden]')) return;
      const item = productCardItem(card);
      if (!item) return;
      impressedCards.add(card);
      impressionObserver?.unobserve(card);
      attributions.push(item);
      const key = `${item.item_list_id || context.pageType}:${item.item_list_name || context.pageTitle}`;
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(item);
    });
    rememberAttributions(attributions);

    groups.forEach((items, key) => {
      pushEcommerceEvent('view_item_list', items, {
        dedupeKey: `list:${key}:${items.map((item) => `${item.item_id}:${item.index}`).join('|')}`,
        dedupeWindow: 60000,
        ecommerce: {
          item_list_id: items[0]?.item_list_id,
          item_list_name: items[0]?.item_list_name,
        },
      });
    });
  };

  const impressionObserver =
    'IntersectionObserver' in window
      ? new IntersectionObserver(
          (entries) => {
            trackCardImpressions(entries.filter((entry) => entry.isIntersecting).map((entry) => entry.target));
          },
          { threshold: 0.25 }
        )
      : null;

  const observeProductCards = (scope = document) => {
    const cards = productCards(scope);
    if (!impressionObserver) {
      trackCardImpressions(
        cards.filter((card) => card.offsetParent !== null && !card.closest('[hidden]'))
      );
      return;
    }
    cards.forEach((card) => {
      observeCardVariantChanges(card);
      if (observedCards.has(card)) return;
      observedCards.add(card);
      impressionObserver.observe(card);
    });
  };

  const cartCoupon = (cart = cartState) =>
    rawItems(cart.discount_codes || cart.cart_level_discount_applications)
      .map((discount) => cleanString(discount?.code || discount?.title || discount))
      .filter(Boolean)
      .join(', ');

  const cartItemsForEvent = (cart = cartState) => {
    const rememberedAttributions = readAttributions();
    const items = rawItems(cart.items)
      .map((item) => applyRememberedAttribution(normalizeItem(item), rememberedAttributions))
      .filter(Boolean);
    const coupon = cartCoupon(cart);
    const itemCoupon = (item) =>
      Array.from(
        new Set(
          [item.coupon, coupon]
            .flatMap((value) => cleanString(value).split(','))
            .map((value) => value.trim())
            .filter(Boolean)
        )
      ).join(', ');

    return items.map((item) => compactObject({ ...item, coupon: itemCoupon(item) }));
  };

  const trackViewCart = () => {
    const items = cartItemsForEvent();
    if (!items.length) return;
    pushEcommerceEvent('view_cart', items, {
      dedupeKey: `view_cart:${cleanString(cartState.token)}:${toNumber(cartState.item_count)}:${toNumber(
        cartState.total_price
      )}`,
      dedupeWindow: 1000,
      ecommerce: {
        coupon: cartCoupon(),
        value: minorToMajor(cartState.total_price),
      },
    });
  };

  const trackBeginCheckout = (cart = cartState) => {
    const items = cartItemsForEvent(cart);
    if (!items.length) return;
    pushEcommerceEvent('begin_checkout', items, {
      dedupeKey: `begin_checkout:${cleanString(cart.token)}:${toNumber(cart.item_count)}:${toNumber(
        cart.total_price
      )}`,
      dedupeWindow: 2500,
      ecommerce: {
        coupon: cartCoupon(cart),
        value: minorToMajor(cart.total_price),
      },
    });
  };

  const observeTabChanges = () => {
    if (!window.MutationObserver || tabObserver) return;
    tabObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        const tabItem = mutation.target;
        if (!(tabItem instanceof Element) || !tabItem.hasAttribute('tab-selected')) return;
        window.requestAnimationFrame(() => observeProductCards(tabItem));
      });
    });
    tabObserver.observe(document.body, {
      attributes: true,
      attributeFilter: ['tab-selected'],
      subtree: true,
    });
  };

  const observeCartMutationRequests = () => {
    if (!window.PerformanceObserver || cartMutationObserver) return;
    try {
      cartMutationObserver = new PerformanceObserver((list) => {
        const hasCartMutation = list.getEntries().some((entry) => {
          if (!['fetch', 'xmlhttprequest'].includes(entry.initiatorType)) return false;
          try {
            return /\/cart\/(?:add|change|update|clear)(?:\.js)?$/.test(
              new URL(entry.name, location.origin).pathname
            );
          } catch (_) {
            return false;
          }
        });
        if (hasCartMutation) reconcileCart();
      });
      cartMutationObserver.observe({ type: 'resource' });
    } catch (_) {
      cartMutationObserver = null;
    }
  };

  const trackInitialPage = () => {
    initializeIdentity();

    if (context.product) {
      const productItem = applyRememberedAttribution(normalizeItem(context.product));
      if (productItem) {
        pushEcommerceEvent('view_item', [productItem], {
          dedupeKey: `view_item:${productItem.item_id}:${location.pathname}${location.search}`,
          dedupeWindow: 60000,
        });
      }
    } else if (context.pageType === 'cart') {
      trackViewCart();
    }

    if (context.searchQuery) pushSearchEvent(context.searchQuery, context.searchResultCount);
    observeProductCards();
    observeTabChanges();
    observeCartMutationRequests();

    const cartDrawer = document.getElementById('CartDrawer');
    cartDrawer?.addEventListener('dialog:open', () => {
      reconcileCart().then(trackViewCart);
    });
  };

  const updateVariantContext = (event) => {
    const card = event.target instanceof Element ? event.target.closest('hdt-card-product') : null;
    const variant = event.detail?.variant;
    if (!variant) return;
    if (card) {
      updateCardVariantContext(card, variant);
      return;
    }
    if (!card && context.product) {
      context.product.variant_id = variant.id || context.product.variant_id;
      context.product.variant_title = variant.title || context.product.variant_title;
      context.product.sku = variant.sku || context.product.sku;
      context.product.price = variant.price ?? context.product.price;
      context.product.original_price =
        variant.compare_at_price || variant.price || context.product.original_price;
    }
  };

  document.addEventListener('variant:updated', updateVariantContext);
  document.addEventListener('variant:change', updateVariantContext);

  document.addEventListener('cart:update', (event) => {
    const detail = event.detail || {};
    const nextCart = detail.cartData;
    trackProductFormAddition(detail);

    if (nextCart?.items && typeof nextCart.item_count === 'number') {
      acceptCartState(nextCart);
    } else {
      reconcileCart();
    }
  });

  document.addEventListener('cart:reload', () => reconcileCart());
  document.addEventListener('cart:drawer:render', () => reconcileCart());

  document.addEventListener('doofinder.cart.add', async (event) => {
    const baselineCount = toNumber(cartState.item_count);
    const variantId = cleanString(event.detail?.item_id);
    const amount = Math.max(1, toNumber(event.detail?.amount) || 1);
    const matchesDoofinderItem = (item) =>
      [item.variant_id, item.id, item.product_id].some(
        (identifier) => cleanString(identifier) === variantId
      );
    const baselineVariantQuantity = rawItems(cartState.items)
      .filter(matchesDoofinderItem)
      .reduce((total, item) => total + toNumber(item.quantity), 0);
    const statusPromise = event.detail?.statusPromise;
    const targetReached = () => {
      const currentVariantQuantity = rawItems(cartState.items)
        .filter(matchesDoofinderItem)
        .reduce((total, item) => total + toNumber(item.quantity), 0);
      return (
        (variantId && currentVariantQuantity >= baselineVariantQuantity + amount) ||
        (!variantId && toNumber(cartState.item_count) >= baselineCount + amount)
      );
    };

    try {
      if (statusPromise?.then) await statusPromise;
      for (const delay of [150, 400, 1000, 2000]) {
        await new Promise((resolve) => window.setTimeout(resolve, delay));
        if (targetReached()) break;
        await reconcileCart();
        if (targetReached()) break;
      }
    } catch (_) {
      // Doofinder failures must not affect shopping or other analytics events.
    }
  });

  document.addEventListener('wishlist:add:success', (event) => {
    const element = event.detail?.element;
    const card = element instanceof Element ? element.closest('hdt-card-product') : null;
    const fallback =
      context.product &&
      String(context.product.product_id) === String(event.detail?.productId || '')
        ? normalizeItem(context.product)
        : null;
    const item = card ? productCardItem(card) : fallback;
    if (!item) return;
    rememberAttribution(item);
    pushEcommerceEvent('add_to_wishlist', [item], {
      dedupeKey: `wishlist:add:${item.item_id}`,
      dedupeWindow: 1500,
    });
  });

  document.addEventListener('products:update', (event) => {
    listContextCache = new WeakMap();
    window.requestAnimationFrame(() => observeProductCards(event.target));
  });

  document.addEventListener('submit', (event) => {
    const submitter = event.submitter;
    if (submitter?.matches?.('button[name="checkout"], input[name="checkout"], [data-checkout]')) {
      trackBeginCheckout();
    }
  });

  document.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    const checkout = target.closest(
      'a[href*="/checkout"], button[name="checkout"], input[name="checkout"], [data-checkout]'
    );
    if (checkout) {
      trackBeginCheckout();
      return;
    }

    const productLink = target.closest(
      'hdt-card-product a[data-pr-url], hdt-card-product a[href*="/products/"]'
    );
    if (!productLink) return;
    const card = productLink.closest('hdt-card-product');
    const item = productCardItem(card);
    if (!item) return;
    rememberAttribution(item);
    pushEcommerceEvent('select_item', [item], {
      dedupeKey: `select_item:${item.item_list_id}:${item.item_id}:${item.index}`,
      dedupeWindow: 1000,
      ecommerce: {
        item_list_id: item.item_list_id,
        item_list_name: item.item_list_name,
      },
    });
  });

  window.addEventListener('pagehide', (event) => {
    if (event.persisted) return;
    impressionObserver?.disconnect();
    tabObserver?.disconnect();
    cartMutationObserver?.disconnect();
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', trackInitialPage, { once: true });
  } else {
    trackInitialPage();
  }
})();
